import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Flex, Form, Select, Tag } from 'antd';
import { Dayjs } from 'dayjs';
import styled from 'styled-components';
import {
  type DataObjectGroupsWithDescriptionsMapById,
  getDayjs,
  getDayjsNow,
  parseGroupDescriptionRef,
  getGroupDescriptionType,
  PeriodEndPicker,
  updateFrequency,
  useGroupDescriptionOptions,
  ValidPeriodOptions,
  valueDate,
  GroupDescriptionRef,
  useSystemClassifierItemById,
  ISODateTime,
  formatDateTimeShort,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { path, UiPathName } from '../../paths';
import translations from './SampleSearch.translations.json';
import {
  colorDraftBg,
  colorDraftBorder,
  colorSuccessBg,
  colorSuccessBorder,
} from '@atlas-frontend/design-tokens';

const { addTranslations, useTranslation } = i18nNsFactory('sample-search');
addTranslations(translations);

export type SampleDataObjectType = 'group' | 'description';

type SearchValues = {
  groupDescriptionRef?: GroupDescriptionRef;
  date?: Dayjs | null;
};

export type SnapshotData = {
  versionStatusId: number | null;
  calculationTime: ISODateTime | null;
};

type Props = {
  treeData?: DataObjectGroupsWithDescriptionsMapById;
  initialValues?: SearchValues;
  isLoading?: boolean;
  uiPathName: UiPathName<'sampleView' | 'inputDataView'>;
  snapshotData?: SnapshotData;
  groupMemberId?: number;
};

/**
 * Component is used in sample & input data directory and detail views
 */
export const SampleSearch: FC<Props> = ({
  treeData,
  initialValues,
  isLoading,
  uiPathName,
  snapshotData,
  groupMemberId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigateTo(path[uiPathName]);

  const [periodOptions, setPeriodOptions] = useState<ValidPeriodOptions>({});

  const [descriptionGroupOptions, ungroupedDescriptionOptions] =
    useGroupDescriptionOptions(treeData);

  const [selectedValues, setSelectedValues] = useState<SearchValues>(
    initialValues ?? {},
  );

  const handleSearch = useCallback(
    (searchValues: SearchValues, selectedGroupMemberId?: number) => {
      setSelectedValues(searchValues);

      const groupDescriptionRef = searchValues.groupDescriptionRef;
      if (!groupDescriptionRef) {
        navigate({
          id: '',
          date: getDayjs(searchValues.date)?.format('YYYY-MM-DD') || '',
        });
        return;
      }

      const { descriptionId, groupId } =
        parseGroupDescriptionRef(groupDescriptionRef);
      const type = getGroupDescriptionType(groupDescriptionRef);
      navigate({
        id: descriptionId || groupId,
        date: getDayjs(searchValues.date)?.format('YYYY-MM-DD') || '',
        type,
        ...(type === 'group' && selectedGroupMemberId !== undefined
          ? { groupMemberId: selectedGroupMemberId }
          : {}),
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (selectedValues.groupDescriptionRef) {
      const { groupId, descriptionId } = parseGroupDescriptionRef(
        selectedValues.groupDescriptionRef,
      );
      updateFrequency(
        getGroupDescriptionType(selectedValues.groupDescriptionRef),
        descriptionId || groupId,
        setPeriodOptions,
        treeData,
      );
    }
  }, [selectedValues, treeData]);

  const handleGroupChange = useCallback(
    (groupDescriptionRef: GroupDescriptionRef) => {
      if (!groupDescriptionRef) {
        handleSearch({ ...selectedValues, groupDescriptionRef: undefined });
        return;
      }

      const { groupId, descriptionId } =
        parseGroupDescriptionRef(groupDescriptionRef);
      const { frequency, validRanges } = updateFrequency(
        getGroupDescriptionType(groupDescriptionRef),
        descriptionId || groupId,
        setPeriodOptions,
        treeData,
      );

      handleSearch({
        ...selectedValues,
        groupDescriptionRef,
        date: valueDate(selectedValues.date ?? null, frequency, validRanges),
      });
    },
    [handleSearch, treeData, selectedValues],
  );

  const handlePeriodChange = useCallback(
    (date: Dayjs | null) => {
      handleSearch({ ...selectedValues, date }, groupMemberId);
    },
    [groupMemberId, handleSearch, selectedValues],
  );

  return (
    <Flex gap="small">
      <SearchWrapper>
        <FormItem
          required
          label={t('field.dataObjectGroup.label')}
          labelCol={{ span: 24 }}
        >
          <Select
            id="select-dataObject"
            placeholder={t('placeholder.select')}
            styles={{
              popup: { root: { width: 'max-content', maxHeight: 400 } },
            }}
            showSearch
            allowClear
            disabled={isLoading}
            loading={isLoading}
            value={isLoading ? undefined : selectedValues.groupDescriptionRef}
            options={
              isLoading
                ? []
                : [
                    {
                      key: 'description-groups',
                      label: t('option-group.description-groups'),
                      options: descriptionGroupOptions,
                    },
                    {
                      key: 'ungrouped-descriptions',
                      label: t('option-group.ungrouped-descriptions'),
                      options: ungroupedDescriptionOptions,
                    },
                  ]
            }
            filterOption={(input, option) =>
              Boolean(
                option?.label?.toLowerCase().includes(input.toLowerCase()),
              )
            }
            onChange={handleGroupChange} // onChange = onSelect & onClear
          />
        </FormItem>
      </SearchWrapper>
      <FormItem
        required
        label={t('field.valueDate.label')}
        labelCol={{ span: 24 }}
      >
        <PeriodEndPicker
          value={selectedValues.date}
          disabled={selectedValues.groupDescriptionRef === undefined}
          disabledDate={periodOptions?.isDisabledDate}
          maxDate={getDayjsNow().add(1, 'year')}
          onChange={handlePeriodChange}
          frequency={periodOptions?.frequency}
        />
      </FormItem>
      {snapshotData && (
        <FormItem label={t('field.version.label')} labelCol={{ span: 24 }}>
          <SnapshotStatusTag {...snapshotData} />
        </FormItem>
      )}
    </Flex>
  );
};

export const SnapshotStatusTag: FC<PropsWithChildren<SnapshotData>> = ({
  versionStatusId,
  calculationTime,
}) => {
  var statusClf = useSystemClassifierItemById(
    'DATA_OBJECT_VER_STATUS',
    versionStatusId || undefined,
  );
  const { t } = useTranslation();

  if (!statusClf) {
    return (
      <UppercaseTag className="ACTIVE">
        {t('field.version.in-use')}
      </UppercaseTag>
    );
  }

  return (
    <UppercaseTag className={statusClf.code}>
      {formatDateTimeShort(calculationTime)} {statusClf.name}
    </UppercaseTag>
  );
};

const SearchWrapper = styled.div`
  max-width: 320px;
  width: 100%;
`;

const FormItem = styled(Form.Item)`
  margin-bottom: 0;
`;

const UppercaseTag = styled(Tag)`
  margin: 0;
  text-transform: uppercase;
  margin-bottom: 9px;

  &:is(.DRAFT) {
    border-color: ${colorDraftBorder};
    background-color: ${colorDraftBg};
  }

  &:is(.ACTIVE),
  &:is(.CONFIRMED),
  &:is(.CONF_PUB),
  &:is(.CONF_PREPUB),
  &:is(.CONF_WAIT) {
    border-color: ${colorSuccessBorder};
    background-color: ${colorSuccessBg};
  }
`;
